import { Component, useState } from "react";
import { SUPPORT_EMAIL, SUPPORT_PHONE_NUMBER } from "../../constants";
import {
  captureBoundaryError,
  classifyClientCrashKind,
  subscribeToClientErrorNotification,
} from "../../services/clientCrashReporter";
import { attemptChunkLoadRecovery } from "../../services/buildRefresh";

const formatSupportPhone = (value = SUPPORT_PHONE_NUMBER) => {
  const digits = String(value || "").replace(/\D/g, "").slice(-10);
  if (digits.length !== 10) {
    return String(value || "");
  }
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const fallbackShellStyles = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px",
  background: "#f8fafc",
  fontFamily:
    "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const fallbackCardStyles = {
  maxWidth: "560px",
  width: "100%",
  border: "1px solid #cbd5e1",
  borderRadius: "16px",
  padding: "28px",
  background: "#ffffff",
  boxShadow: "0 20px 60px rgba(15, 23, 42, 0.18)",
};

const buttonBaseStyles = {
  appearance: "none",
  borderRadius: "12px",
  padding: "12px 18px",
  fontSize: "15px",
  fontWeight: 600,
  cursor: "pointer",
};

const inputStyles = {
  width: "100%",
  boxSizing: "border-box",
  marginTop: "12px",
  border: "1px solid #cbd5e1",
  borderRadius: "12px",
  padding: "12px 14px",
  fontSize: "15px",
  color: "#0f172a",
};

function CrashNotificationForm({ issueId = null, groupId = null, incidentId = null }) {
  const [target, setTarget] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [submittingChannel, setSubmittingChannel] = useState(null);

  if (!issueId && !groupId) {
    return (
      <p
        style={{
          margin: "16px 0 0",
          fontSize: "14px",
          lineHeight: 1.5,
          color: "#475569",
        }}
      >
        We've notified our software team and we're actively working on a fix.
      </p>
    );
  }

  const submit = async (channel) => {
    setSubmittingChannel(channel);
    setStatus("");
    setError("");

    try {
      const response = await subscribeToClientErrorNotification({
        issueId,
        groupId,
        incidentId,
        channel,
        target,
      });
      setStatus(response?.message || "We'll let you know when it's fixed.");
    } catch (submitError) {
      setError("Enter a valid email or mobile number.");
    } finally {
      setSubmittingChannel(null);
    }
  };

  return (
    <div
      style={{
        marginTop: "20px",
        padding: "16px",
        border: "1px solid #e2e8f0",
        borderRadius: "16px",
        background: "#f8fafc",
      }}
    >
      <p
        style={{
          margin: 0,
          fontSize: "14px",
          lineHeight: 1.5,
          color: "#334155",
        }}
      >
        We've notified our software team and we're actively working on a fix.
        Leave your email or mobile number and we'll let you know when it's fixed.
      </p>
      <input
        type="text"
        inputMode="email"
        value={target}
        onChange={(event) => setTarget(event.target.value)}
        placeholder="Email or mobile number"
        style={inputStyles}
      />
      <div
        style={{
          display: "flex",
          gap: "12px",
          flexWrap: "wrap",
          marginTop: "12px",
        }}
      >
        <button
          onClick={() => void submit("email")}
          disabled={Boolean(submittingChannel)}
          style={{
            ...buttonBaseStyles,
            border: "1px solid #cbd5e1",
            background: "#ffffff",
            color: "#0f172a",
            padding: "10px 16px",
            fontSize: "14px",
          }}
        >
          {submittingChannel === "email" ? "Sending..." : "Email me"}
        </button>
        <button
          onClick={() => void submit("sms")}
          disabled={Boolean(submittingChannel)}
          style={{
            ...buttonBaseStyles,
            border: "1px solid #cbd5e1",
            background: "#ffffff",
            color: "#0f172a",
            padding: "10px 16px",
            fontSize: "14px",
          }}
        >
          {submittingChannel === "sms" ? "Sending..." : "Text me"}
        </button>
      </div>
      {status ? (
        <p
          style={{
            margin: "10px 0 0",
            fontSize: "13px",
            color: "#475569",
          }}
        >
          {status}
        </p>
      ) : null}
      {error ? (
        <p
          style={{
            margin: "10px 0 0",
            fontSize: "13px",
            color: "#b91c1c",
          }}
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}

function AppCrashFallback({ incidentId = null, issueId = null, groupId = null }) {
  return (
    <div style={fallbackShellStyles}>
      <div style={fallbackCardStyles}>
        <p
          style={{
            margin: "0 0 8px",
            fontSize: "12px",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "#64748b",
          }}
        >
          Linked Lives
        </p>
        <h1
          style={{
            margin: 0,
            fontSize: "28px",
            lineHeight: 1.1,
            color: "#0f172a",
          }}
        >
          Something went wrong loading this page.
        </h1>
        <p
          style={{
            margin: "16px 0 0",
            fontSize: "16px",
            lineHeight: 1.5,
            color: "#334155",
          }}
        >
          We recorded the problem. Please refresh the page. If it keeps
          happening, our software team has been notified and is actively
          working on a fix.
        </p>
        {incidentId ? (
          <p
            style={{
              margin: "12px 0 0",
              fontSize: "13px",
              color: "#475569",
            }}
          >
            Incident ID:{" "}
            <code
              style={{
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              }}
            >
              {incidentId}
            </code>
          </p>
        ) : null}
        <CrashNotificationForm
          issueId={issueId}
          groupId={groupId}
          incidentId={incidentId}
        />
        <div
          style={{
            display: "flex",
            gap: "12px",
            flexWrap: "wrap",
            marginTop: "24px",
          }}
        >
          <button
            onClick={() => window.location.reload()}
            style={{
              ...buttonBaseStyles,
              border: "none",
              background: "#0f172a",
              color: "#ffffff",
            }}
          >
            Reload page
          </button>
          <button
            onClick={() => window.location.assign("/")}
            style={{
              ...buttonBaseStyles,
              border: "1px solid #cbd5e1",
              background: "#ffffff",
              color: "#0f172a",
            }}
          >
            Go to homepage
          </button>
        </div>
        <p
          style={{
            margin: "18px 0 0",
            fontSize: "14px",
            lineHeight: 1.5,
            color: "#475569",
          }}
        >
          Support:{" "}
          <a href={`tel:${SUPPORT_PHONE_NUMBER}`} style={{ color: "#0f172a" }}>
            {formatSupportPhone(SUPPORT_PHONE_NUMBER)}
          </a>{" "}
          •{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: "#0f172a" }}>
            {SUPPORT_EMAIL}
          </a>
        </p>
      </div>
    </div>
  );
}

function ChunkLoadRecoveryFallback() {
  return (
    <div style={fallbackShellStyles}>
      <div style={fallbackCardStyles}>
        <p
          style={{
            margin: "0 0 8px",
            fontSize: "12px",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "#64748b",
          }}
        >
          Linked Lives
        </p>
        <h1
          style={{
            margin: 0,
            fontSize: "28px",
            lineHeight: 1.1,
            color: "#0f172a",
          }}
        >
          Refreshing this page.
        </h1>
        <p
          style={{
            margin: "16px 0 0",
            fontSize: "16px",
            lineHeight: 1.5,
            color: "#334155",
          }}
        >
          The app needs a fresh copy of this screen.
        </p>
        <div style={{ display: "flex", marginTop: "24px" }}>
          <button
            onClick={() => window.location.reload()}
            style={{
              ...buttonBaseStyles,
              border: "none",
              background: "#0f172a",
              color: "#ffffff",
            }}
          >
            Reload page
          </button>
        </div>
      </div>
    </div>
  );
}

class AppCrashBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      incidentId: null,
      issueId: null,
      groupId: null,
      isChunkLoadFailure: false,
      isFirestoreClientFailure: false,
    };
  }

  static getDerivedStateFromError(error) {
    const classification = classifyClientCrashKind({
      name: error?.name,
      message: error?.message,
      stack: error?.stack,
      source: "react_boundary",
    });

    return {
      hasError: true,
      isChunkLoadFailure: classification === "chunk_load_failure",
      isFirestoreClientFailure: classification === "firestore_client_failure",
    };
  }

  componentDidCatch(error, info) {
    const classification = classifyClientCrashKind({
      name: error?.name,
      message: error?.message,
      stack: error?.stack,
      source: "react_boundary",
    });

    if (classification === "chunk_load_failure") {
      void attemptChunkLoadRecovery();
      return;
    }

    Promise.resolve(
      captureBoundaryError(error, {
        componentStack: info?.componentStack || null,
      }),
    )
      .then((result) => {
        if (result?.recoveryScheduled) {
          return;
        }

        const recoveryState =
          classification === "firestore_client_failure"
            ? { isFirestoreClientFailure: false }
            : null;

        if (result?.incidentId || result?.issueId || result?.groupId) {
          this.setState({
            ...recoveryState,
            incidentId: result?.incidentId || null,
            issueId: result?.issueId || null,
            groupId: result?.groupId || null,
          });
          return;
        }

        if (recoveryState) {
          this.setState(recoveryState);
        }
      })
      .catch(() => {});
  }

  render() {
    if (this.state.hasError) {
      if (this.state.isChunkLoadFailure) {
        return <ChunkLoadRecoveryFallback />;
      }

      if (this.state.isFirestoreClientFailure) {
        return <ChunkLoadRecoveryFallback />;
      }

      return (
        <AppCrashFallback
          incidentId={this.state.incidentId}
          issueId={this.state.issueId}
          groupId={this.state.groupId}
        />
      );
    }

    return this.props.children;
  }
}

export default AppCrashBoundary;
export { AppCrashFallback, ChunkLoadRecoveryFallback };
